/**
 * DOM contract:
 *   [data-description-body]   — the clamped rich-text container
 *   [data-description-toggle] — the read more/less button
 */

const ANIMATION_DURATION = 300;
const ANIMATION_EASING = "cubic-bezier(0.35, 0, 0.15, 1)";

class DescriptionClamp extends HTMLElement {
  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  /** @type {number} */
  #lastWidth = 0;

  /** @type {MutationObserver | null} */
  #mutationObserver = null;

  /** @type {Animation | undefined} */
  #animation;

  connectedCallback() {
    this.#toggle?.addEventListener("click", this.#handleToggle);
    this.#resizeObserver = new ResizeObserver(this.#handleResize);
    if (this.#body) this.#resizeObserver.observe(this.#body);
    this.#mutationObserver = new MutationObserver(this.#syncOverflow);
    this.#mutationObserver.observe(this, {
      attributes: true,
      attributeFilter: ["data-can-expand"],
      childList: true,
      characterData: true,
      subtree: true
    });
    this.#syncOverflow();
  }

  disconnectedCallback() {
    this.#toggle?.removeEventListener("click", this.#handleToggle);
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;
    this.#mutationObserver?.disconnect();
    this.#mutationObserver = null;
    this.#animation?.cancel();
  }

  get #body() {
    return /** @type {HTMLElement | null} */ (this.querySelector("[data-description-body]"));
  }

  get #toggle() {
    return /** @type {HTMLButtonElement | null} */ (this.querySelector("[data-description-toggle]"));
  }

  get #expanded() {
    return this.hasAttribute("data-expanded");
  }

  get #prefersReducedMotion() {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  }

  /**
   * Only react to width changes. Height changes are self-inflicted by the expand/collapse
   * transition; measuring during them would drop `data-can-expand` and make the toggle flicker.
   * @param {ResizeObserverEntry[]} entries
   */
  #handleResize = (entries) => {
    const width = Math.round(entries[0]?.contentRect.width ?? 0);
    if (width === this.#lastWidth) return;
    this.#lastWidth = width;
    this.#syncOverflow();
  };

  #syncOverflow = () => {
    const body = this.#body;
    if (!body || this.#expanded) return;

    const canExpand = body.scrollHeight > body.clientHeight + 1;
    this.toggleAttribute("data-can-expand", canExpand);
    if (this.#toggle) this.#toggle.disabled = !canExpand;
  };

  #handleToggle = () => {
    const body = this.#body;
    if (!body) return;

    const next = !this.#expanded;
    const startHeight = body.getBoundingClientRect().height;

    this.toggleAttribute("data-expanded", next);
    this.#toggle?.setAttribute("aria-expanded", String(next));

    if (this.#prefersReducedMotion) return;

    const endHeight = body.getBoundingClientRect().height;

    this.#animation?.cancel();
    this.#animation = body.animate(
      { maxHeight: [`${startHeight}px`, `${endHeight}px`] },
      { duration: ANIMATION_DURATION, easing: ANIMATION_EASING }
    );
  };
}

if (!customElements.get("description-clamp")) {
  customElements.define("description-clamp", DescriptionClamp);
}
